import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import './PickleballFitness.css';

// Deterministic spots based on day-of-month + month combo
// Feels random but is consistent for any given date
function getSpotsForDate(date) {
  const d = date.getDate();
  const m = date.getMonth() + 1;
  const seed = (d * 3 + m * 7) % 12;
  const spots = [0, 3, 12, 7, 11, 0, 9, 12, 5, 10, 0, 8];
  return spots[seed];
}

function getUpcomingDates() {
  const now = new Date();
  const days = [];
  for (let i = 0; i < 28; i++) {
    const date = new Date(now);
    date.setDate(now.getDate() + i);
    const dow = date.getDay();
    if (dow === 1 || dow === 4) {
      const spots = getSpotsForDate(date);
      days.push({
        dayName: dow === 1 ? 'Monday' : 'Thursday',
        time: dow === 1 ? '4:00 – 5:00 PM' : '9:00 – 10:00 AM',
        className: dow === 1 ? 'Dink & Tone' : 'Smash & Sculpt',
        instructor: 'Jen D.',
        short: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        full: date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
        spots,
        isFull: spots === 0,
      });
    }
    if (days.length >= 8) break;
  }
  // Add a couple classes from other instructors
  const wed = new Date(now);
  for (let i = 0; i < 14; i++) {
    wed.setDate(now.getDate() + i);
    if (wed.getDay() === 3) { // Wednesday
      days.push({
        dayName: 'Wednesday',
        time: '6:00 – 7:00 PM',
        className: 'Court Conditioning',
        instructor: 'Mike R.',
        short: wed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        spots: getSpotsForDate(wed),
        isFull: getSpotsForDate(wed) === 0,
      });
      break;
    }
  }
  const sat = new Date(now);
  for (let i = 0; i < 14; i++) {
    sat.setDate(now.getDate() + i);
    if (sat.getDay() === 6) { // Saturday
      days.push({
        dayName: 'Saturday',
        time: '8:00 – 9:00 AM',
        className: 'Power & Paddle',
        instructor: 'Lisa T.',
        short: sat.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        spots: getSpotsForDate(sat),
        isFull: getSpotsForDate(sat) === 0,
      });
      break;
    }
  }
  // Sort by date
  days.sort((a, b) => {
    const dateA = new Date(a.short + ', 2026');
    const dateB = new Date(b.short + ', 2026');
    return dateA - dateB;
  });
  return days;
}

const fade = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

export default function PickleballFitness() {
  const schedule = useMemo(() => getUpcomingDates(), []);

  return (
    <div className="pf">

      {/* Nav */}
      <nav className="pf-nav">
        <div className="pf-nav-inner">
          <span className="pf-nav-brand">Pickleball Fitness</span>
          <div className="pf-nav-links">
            <a href="#about">About</a>
            <a href="#classes">Classes</a>
            <a href="#schedule">Schedule</a>
            <a href="#details">Info</a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pf-hero">
        <div className="pf-hero-bg">
          <img src="/images/pickleball-fitness/group.jpg" alt="Pickleball fitness class" className="pf-hero-img" />
        </div>
        <motion.div className="pf-hero-content" initial="hidden" animate="visible" variants={fade}>
          <p className="pf-hero-eyebrow">The Oaks &middot; Calabasas</p>
          <h1>Pickleball<br />Fitness</h1>
          <p className="pf-hero-tagline">Move Better. Play Longer. Own the Court.</p>
          <a href="#schedule" className="pf-btn">See the Schedule</a>
        </motion.div>
      </section>

      {/* About */}
      <section className="pf-about" id="about">
        <motion.div className="pf-about-inner" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fade}>
          <div className="pf-about-photo-slot">
            <img src="/images/pickleball-fitness/instructor.jpg" alt="Jennifer de Nicola" className="pf-about-img" />
          </div>
          <div className="pf-about-text">
            <h2>Hi, I'm Jennifer.</h2>
            <p>
              When I
              discovered pickleball a few years ago, I fell in love with the game
              but quickly noticed something: so many players were getting hurt or
              hitting walls because they weren't training their bodies for the
              specific demands of the sport.
            </p>
            <p>
              That's why I created Pickleball Fitness. Every class is built around
              the movements you actually make on the court — lateral agility,
              shoulder stability, core rotation, quick footwork. I blend strength
              training, stretching, and injury prevention into the perfect
              supplemental workout so you can play longer, move better, and stay
              pain-free.
            </p>
            <p>
              Whether you just picked up a paddle or you're competing in
              tournaments, I'd love to have you in class.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Classes + Schedule side by side */}
      <section className="pf-split" id="classes">
        <motion.div className="pf-split-inner" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} variants={fade}>

          {/* Left: Classes */}
          <div className="pf-split-left">
            <h2>Class Offerings</h2>
            <p className="pf-split-sub">Each class hits a different part of your game. Drop into any — or try them all.</p>
            <div className="pf-class-list">
              {[
                { name: 'Dink & Tone', desc: 'Low-impact toning paired with footwork drills for kitchen-line endurance.' },
                { name: 'Smash & Sculpt', desc: 'Upper body and core strength for power shots and overhead smashes.' },
                { name: 'Kitchen Cardio', desc: 'Agility intervals and lateral movement drills to sharpen your court speed.' },
                { name: 'Rally & Stretch', desc: 'Deep stretching and mobility for shoulders, hips, and ankles — stay loose, stay playing.' },
                { name: 'Net Gains', desc: 'Full-body functional strength built around pickleball movement patterns.' },
                { name: 'Volley Flow', desc: 'Yoga-inspired flows and dynamic stretching. Great for recovery days.' },
                { name: 'Court Conditioning', desc: 'High-intensity drills combining sprints, shuffles, and resistance bands for total court fitness.' },
                { name: 'Power & Paddle', desc: 'Grip strength, wrist stability, and rotational power — built for players who want more pop on every shot.' },
              ].map((c) => (
                <div className="pf-class-item" key={c.name}>
                  <h3>{c.name}</h3>
                  <p>{c.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Schedule */}
          <div className="pf-split-right" id="schedule">
            <h2>Upcoming Schedule</h2>
            <p className="pf-split-sub">Every week at The Oaks, Calabasas.</p>

            <div className="pf-schedule-dates">
              <div className="pf-date-list">
                {schedule.map((s, i) => (
                  <div className={`pf-date-row${s.isFull ? ' pf-date-full' : ''}`} key={i}>
                    <span className="pf-date-badge">{s.short}</span>
                    <span className="pf-date-info">
                      <span className="pf-date-class-name">{s.className}</span>
                      {s.dayName} &middot; {s.time} &middot; w/ {s.instructor}
                    </span>
                    <span className={`pf-date-spots${s.isFull ? ' pf-spots-full' : s.spots <= 4 ? ' pf-spots-low' : ''}`}>
                      {s.isFull ? 'Full' : `${s.spots} spots`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </motion.div>
      </section>

      {/* Private & Beginner Lessons */}
      <section className="pf-private">
        <motion.div className="pf-private-inner" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fade}>
          <div className="pf-private-card">
            <h3>Private Lessons</h3>
            <p>
              One-on-one sessions tailored to your game. Whether you want to sharpen
              a specific skill, work on strategy, or recover from an injury with guided
              training — private lessons are built entirely around you.
            </p>
            <a href="mailto:fitness@jendenicola.com" className="pf-private-btn">Get in Touch</a>
          </div>
          <div className="pf-private-card">
            <h3>Beginner Lessons</h3>
            <p>
              New to pickleball? Start here. Learn the fundamentals — grip, stance,
              scoring, basic shots, and court positioning — in a relaxed, supportive
              setting. No experience needed.
            </p>
            <a href="mailto:fitness@jendenicola.com" className="pf-private-btn">Get in Touch</a>
          </div>
        </motion.div>
      </section>

      {/* Details */}
      <section className="pf-details" id="details">
        <motion.div className="pf-details-inner" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fade}>
          <h2>What to Know</h2>
          <div className="pf-details-grid">
            <div className="pf-detail">
              <strong>$35 per class</strong>
              <span>Drop in anytime, no commitment</span>
            </div>
            <div className="pf-detail">
              <strong>Wear tennis shoes</strong>
              <span>Supportive shoes with good grip</span>
            </div>
            <div className="pf-detail">
              <strong>Bring a yoga mat</strong>
              <span>For stretching and floor work</span>
            </div>
            <div className="pf-detail">
              <strong>All levels welcome</strong>
              <span>Beginners to competitive players</span>
            </div>
            <div className="pf-detail">
              <strong>Paddles & balls provided</strong>
              <span>Feel free to bring your own</span>
            </div>
            <div className="pf-detail">
              <strong>The Oaks, Calabasas</strong>
              <span>Calabasas, CA</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Photo */}
      <section className="pf-photo-break">
        <img src="/images/pickleball-fitness/hero.jpg" alt="Pickleball fitness group on the court" className="pf-photo-break-img" />
      </section>

      {/* CTA */}
      <section className="pf-cta">
        <motion.div className="pf-cta-inner" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fade}>
          <h2>Come move with me.</h2>
          <p>Spots fill up fast — only 12 per class. Save yours below.</p>
          <a href="mailto:fitness@jendenicola.com" className="pf-btn">Get in Touch</a>
          <p className="pf-cta-email">fitness@jendenicola.com</p>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="pf-footer">
        <p className="pf-footer-brand">Pickleball Fitness</p>
        <p>with Jennifer de Nicola &middot; The Oaks, Calabasas, CA</p>
      </footer>
    </div>
  );
}
